import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

// MUI v4
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

// Webapps / Notion
import { updateTutorialState } from '@webapps/business';
import {
  Loader,
  ModalSideBar,
  useAuth,
  useMutation,
} from '@webapps/reactjs-tech-components';
import { useStyles as useGlobalStyles } from '@webapps/styles';
import { ROUTES_PATH } from 'config/constants';

// Composants locaux
import Card from './components/Card';
import FAQDetail from './components/FAQDetail';
import { PlayArrowRounded } from '@material-ui/icons';

/**
 * Construit la liste des cartes FAQ à partir des blocs Notion.
 */
function buildCardsFromBlocks(blocks) {
  const cards = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === 'child_page') {
      const newCard = {
        id: block.id,
        title: block.child_page.title,
        icon: '',
        description: '',
      };
      let j = i + 1;
      while (j < blocks.length && blocks[j].type !== 'child_page') {
        const subBlock = blocks[j];
        if (subBlock.type === 'file') {
          newCard.icon = subBlock.file.file.url;
        } else if (subBlock.type === 'callout') {
          newCard.description =
            subBlock.callout.rich_text?.[0]?.plain_text || '';
        }
        j++;
      }
      cards.push(newCard);
      i = j;
    } else {
      i++;
    }
  }
  return cards;
}

export default function FAQ() {
  const classes = useStyles();
  const { classes: global, cx } = useGlobalStyles();

  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [blockId, setBlockId] = useState('');
  const [title, setTitle] = useState('');

  const { idUser, profile, isPremium } = useAuth();
  const { push } = useHistory();

  const { isSuccess, mutateAsync: mutateUpdateTutorialState } =
    useMutation(updateTutorialState);

  /**
   * Permet de relancer le tutoriel
   */
  const handleReplayTutorial = async () => {
    try {
      await mutateUpdateTutorialState({ id: idUser, state: false });
    } catch (error) {
      console.error('Erreur lors du redémarrage du tutoriel', error);
    }
  };

  /**
   * Récupère les données Notion selon le profil
   */
  async function getNotionPages() {
    const { NX_PROXY_API } = process.env;
    let response;

    if (profile === 'BUSINESS' && !isPremium) {
      response = await fetch(
        `${NX_PROXY_API}/notion/faq/business/1a1c73bd2072811fa942e58ccc23b4d9`
      );
    } else if (profile === 'SCHOOL' && !isPremium) {
      response = await fetch(
        `${NX_PROXY_API}/notion/faq/school/1a1c73bd2072812187f0c9719a7fb798`
      );
    } else if (profile === 'INDIVIDUAL') {
      response = await fetch(
        `${NX_PROXY_API}/notion/faq/student/1a1c73bd207281559f63c4623b5d5e2c`
      );
    } else if (profile === 'BUSINESS' && isPremium) {
      response = await fetch(
        `${NX_PROXY_API}/notion/faq/businessPremium/1a1c73bd20728178b3a9c4ccc54b7167`
      );
    } else if (profile === 'SCHOOL' && isPremium) {
      response = await fetch(
        `${NX_PROXY_API}/notion/faq/schoolPremium/1a1c73bd2072811596c8c25880b00f10`
      );
    } else if (profile === 'CFA') {
      response = await fetch(`${NX_PROXY_API}/cfa/todefine`);
    }

    if (!response) return;
    const json = await response.json();
    if (!json.success || !Array.isArray(json.data)) {
      console.log('Réponse Notion inattendue :', json);
      return;
    }
    setCategories(json.data);
  }

  const handleCloseModal = () => {
    setOpen(false);
    setBlockId('');
  };

  useEffect(() => {
    getNotionPages();
  }, [isPremium]);

  useEffect(() => {
    if (isSuccess) {
      if (profile === 'BUSINESS') {
        push({ pathname: ROUTES_PATH.BUSINESS_FAQ, state: { tutorial: true } });
      } else if (profile === 'SCHOOL') {
        push({ pathname: ROUTES_PATH.SCHOOL_FAQ, state: { tutorial: true } });
      } else if (profile === 'INDIVIDUAL') {
        push({ pathname: ROUTES_PATH.STUDENT_FAQ, state: { tutorial: true } });
      }
      if(profile === "CFA"){
        push({pathname: ROUTES_PATH.CFA_FAQ, state: { tutorial: true }})
      }
    }
  }, [isSuccess, profile, push]);

  return (
    <div className={classes.container}>
      {/* Conteneur flex pour la sidebar et le main content */}
      <div className={classes.contentWrapper}>
        {/* ---- SIDEBAR ---- */}
        <div className={classes.sidebar}>
          {/* 
            Au lieu de séparer "Rejouer le tutoriel" du reste,
            on le met dans le même conteneur que les boutons "Être accompagné".
          */}
          <div className={classes.sideButtonsContainer}>
            <Button
              variant="outlined"
              startIcon={<PlayArrowRounded />}
              onClick={handleReplayTutorial}
              className={cx(classes.tutorialButton)}
            >
              Rejouer le tutoriel
            </Button>

            {categories?.length > 0 && (
              <div className={classes.displayNone}>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    onClick={() => {
                      const section = document.getElementById(`cat-${cat.id}`);
                      section?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={cx(classes.sideButton)}
                  >
                    {cat.title.length > 20 ? cat.title.substring(0, 20) + '...' : cat.title}
                  </Button>
                ))}
              </div>
              )}
          </div>
        </div>

        {/* ---- MAIN CONTENT ---- */}
        <div className={classes.mainContent}>
          <div className={classes.faqContainer}>

            {categories?.length > 0 ? categories.map((cat) => {
              const catCards = buildCardsFromBlocks(cat.children || []);
              return (
                <div key={cat.id} id={`cat-${cat.id}`} className={classes.catBlock}>
                  <Typography
                    variant="h6"
                    className={cx(classes.categoryTitle, global.title)}
                  >
                    {cat.title}
                  </Typography>

                  {/* Grid MUI transformée en CSS Grid */}
                  <Grid container className={classes.gridContainer} spacing={0}>
                    {catCards.map((card) => (
                      // On n'utilise plus xs={12} md={6}, c'est géré par CSS
                      <Grid item key={card.id} className={classes.gridItem}>
                        <Card
                          icon={card.icon}
                          title={card.title}
                          subtitle={card.description}
                          onClick={() => {
                            setOpen(true);
                            setBlockId(card.id);
                            setTitle(card.title);
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </div>
              );
            }) : <div className={classes.loader}><Loader /></div>}
          </div>
        </div>
      </div>

      {/* ---- MODAL latérale ---- */}
      <ModalSideBar
        handleClose={handleCloseModal}
        open={open}
        title={title}
        titleSize="20px"
        width="700px"
      >
        <FAQDetail
          onCancel={handleCloseModal}
          open={open}
          blockId={blockId}
          profile={profile}
          isPremium={isPremium}
        />
      </ModalSideBar>
    </div>
  );
}

/** 
 * Styles MUI v4
 */
const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex', 
    gap: '50px',
    padding: '50px',
    '@media only screen and (max-width:1543px)': { padding: '42px' },
    '@media only screen and (max-width:1440px)': { padding: '34px' },
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  contentWrapper: {
    display: 'flex',
    gap: '50px',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  sidebar: {
    position: 'sticky',
    top: 50,
    width: '18%',
    height: 'fit-content',
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      position: 'static',
      width: '100%',
      padding: '0',
      backgroundColor: 'transparent',
    },
  },
  displayNone: {
    display: 'flex',
    flexDirection: 'column',
    gap: "10px",
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  // Conteneur pour TOUS les boutons de la sidebar
  sideButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // Permet d'empiler "Rejouer le tutoriel" + cat.title
  },

  tutorialButton: {
    backgroundColor: '#0060F9',
    color: '#FFFFFF',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#0060F9',
    },
  },
  sideButton: {
    width: '100%',
    padding: theme.spacing(1.5),
    textAlign: 'left',
    borderRadius: '30px',
    border: '1px solid #C0D9FF',
    backgroundColor: '#F3F7FF',
    color: '#5D75A8',
    cursor: 'pointer',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#FFFFFF',
      color: '#152652',
      fontWeight: 600,
    },
  },

  mainContent: {
    flex: 1,
  },
  faqContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '60px', // Espace vertical entre chaque "catBlock"
  },
  loader: {
    // backgroundColor: 'red',
    height: '50vh',
  },
  catBlock: {
    // Pas de marginBottom, géré par gap
  },
  categoryTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: '#152652',
  },

  /** 
   * On force le container MUI en CSS Grid
   * 1 colonne par défaut
   * 2 colonnes à partir de "md" (960px)
   */
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr', // Par défaut, 1 colonne
    gap: '16px',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    margin: 0,
  },
  gridItem: {
    // MUI n'ajoute pas de padding si spacing={0}
  },
}));
